import { motion } from "framer-motion";
import { CalendarDays, MoreHorizontal, Pencil, Trash2, CircleDashed, Clock, CheckCircle2, AlertTriangle } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { STATUS_META, formatDate, isOverdue } from "../lib/status";
import { cn } from "../lib/utils";

const statusIcon = {
  todo: CircleDashed,
  "in-progress": Clock,
  done: CheckCircle2,
};

export const TaskCard = ({ task, onEdit, onDelete, onStatusChange }) => {
  const meta = STATUS_META[task.status];
  const Icon = statusIcon[task.status];
  const overdue = isOverdue(task.due_date, task.status);

  const toggleDone = (checked) => {
    const next = checked ? "done" : task.status === "done" ? "todo" : task.status;
    if (next !== task.status) onStatusChange(task, next);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      data-testid={`task-card-${task.id}`}
      className="group relative bg-white dark:bg-[#181E2C] border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700"
      style={{ transitionProperty: "box-shadow, border-color", transitionDuration: "200ms" }}
    >
      <div className="flex gap-4 items-start">
        <div className="pt-0.5">
          <Checkbox
            checked={task.status === "done"}
            onCheckedChange={toggleDone}
            data-testid={`task-checkbox-${task.id}`}
            className="h-5 w-5 rounded-md"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 justify-between">
            <div className="min-w-0">
              <h3
                data-testid={`task-title-${task.id}`}
                className={cn(
                  "font-display text-base font-semibold text-slate-900 dark:text-slate-100 line-clamp-1",
                  task.status === "done" && "line-through text-slate-400 dark:text-slate-500"
                )}
              >
                {task.title}
              </h3>
              {task.description && (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                  {task.description}
                </p>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-70 group-hover:opacity-100"
                  data-testid={`task-menu-${task.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => onEdit(task)} data-testid={`task-edit-btn-${task.id}`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onStatusChange(task, "todo")}
                  disabled={task.status === "todo"}
                  data-testid={`task-move-todo-${task.id}`}
                >
                  <CircleDashed className="mr-2 h-4 w-4" />
                  Move to Todo
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onStatusChange(task, "in-progress")}
                  disabled={task.status === "in-progress"}
                  data-testid={`task-move-progress-${task.id}`}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Mark In Progress
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onStatusChange(task, "done")}
                  disabled={task.status === "done"}
                  data-testid={`task-move-done-${task.id}`}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Mark Done
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-rose-600 focus:text-rose-600"
                  onClick={() => onDelete(task)}
                  data-testid={`task-delete-btn-${task.id}`}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span
              data-testid={`task-status-badge-${task.id}`}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                meta.pill
              )}
            >
              <Icon className="h-3 w-3" />
              {meta.label}
            </span>

            {task.due_date && (
              <span
                data-testid={`task-due-date-${task.id}`}
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                  overdue
                    ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/60"
                    : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700"
                )}
              >
                {overdue ? <AlertTriangle className="h-3 w-3" /> : <CalendarDays className="h-3 w-3" />}
                {formatDate(task.due_date)}
                {overdue && <span className="ml-0.5">· overdue</span>}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
